import React, { useMemo } from "react";
import { HandleInputChangeRequest, UpdatedARIValue, ValueTypes } from "../shared/types";
import { CustomTableVirtuoso } from "../shared/table";
import { Inventory } from "./domain/Inventory";
import { InventoryData } from "../shared/domain/InventoryData";
import { NAME_TYPE } from "../shared/constants";
import { PromotionData } from "../shared/domain/PromotionData";
import { ExtraData } from "../shared/domain/ExtraData";
import { Promotion } from "./domain/Promotion";
import { Extra } from "./domain/Extra";
import { indexOf } from "lodash";
import { AriType } from "../updateForm";
import { BundleInventoryData } from "../shared/domain/BundleInventoryData";
import { BundleInventory } from "./domain/BundleInventory";
import { usePropertyConfiguration } from "../../../components/property-configuration-provider";

interface Props {
  items: InventoryData[];
  promotions: PromotionData[];
  extras: ExtraData[];
  bundleInventories: BundleInventoryData[];
  handleQuickUpdate: (ticker: string, valueType: ValueTypes, type: AriType) => void;
  enableEdition: boolean;
  handleInputChange: (props: HandleInputChangeRequest) => void;
  nameType: NAME_TYPE;
  updatedARIValues: UpdatedARIValue;
  showAltStyles: boolean;
  numberOfDays: number;
}

const valueTypesOrder = [
  ValueTypes.rate,
  ValueTypes.availability,
  ValueTypes.closed,
  ValueTypes.closeToArrival,
  ValueTypes.closeToDeparture,
  ValueTypes.minStay,
  ValueTypes.maxStay,
  ValueTypes.minNotice,
  ValueTypes.maxNotice,
];

const sortByValueType = (a: ValueTypes, b: ValueTypes) =>
  indexOf(valueTypesOrder, a) - indexOf(valueTypesOrder, b);

export const Inventories = ({
  items,
  promotions,
  extras,
  bundleInventories,
  handleQuickUpdate,
  enableEdition,
  handleInputChange,
  nameType,
  updatedARIValues,
  showAltStyles,
  numberOfDays,
}: Props): React.ReactElement => {
  const inventoryList = useMemo(
    (): Inventory[] =>
      items
        .map(data =>
          Object.entries(data.dailyValues)
            .map(([valueType, values]) =>
              Inventory.of(
                data.ticker,
                data.name,
                data.configuration,
                values?.parentTicker,
                values?.dailySet,
                valueType as ValueTypes,
                data.isChild,
                values?.formula
              )
            )
            .sort((a: any, b: any) => sortByValueType(a.valueType, b.valueType))
        )
        .reduce((acc, values) => {
          values.forEach(inventory => {
            acc.push(inventory);
          });
          return acc;
        }, []),
    [items]
  );

  const promotionList = useMemo(
    (): Promotion[] =>
      promotions
        .map(data =>
          Object.entries(data.dailyValues)
            .map(([valueType, values]) =>
              Promotion.of(data.ticker, valueType as ValueTypes, values.dailySet)
            )
            .sort((a: any, b: any) => sortByValueType(a.valueType, b.valueType))
        )
        .reduce((acc, values) => {
          values.forEach(inventory => {
            acc.push(inventory);
          });
          return acc;
        }, []),
    [promotions]
  );

  const extraList = useMemo(
    (): Extra[] =>
      extras
        .map(data =>
          Object.entries(data.dailyValues)
            .map(([valueType, values]) =>
              Extra.of(data.ticker, valueType as ValueTypes, values.dailySet)
            )
            .sort((a: any, b: any) => sortByValueType(a.valueType, b.valueType))
        )
        .reduce((acc, values) => {
          values.forEach(inventory => {
            acc.push(inventory);
          });
          return acc;
        }, []),
    [extras]
  );

  const bundleInventoryList = useMemo(
    (): Inventory[] =>
      bundleInventories
        .map(data =>
          Object.entries(data.dailyValues)
            .map(([valueType, values]) =>
              BundleInventory.of(
                data.ticker,
                data.name,
                data.configuration,
                values?.parentTicker,
                values?.dailySet,
                valueType as ValueTypes,
                values?.formula
              )
            )
            .sort((a: any, b: any) => sortByValueType(a.valueType, b.valueType))
        )
        .reduce((acc, values) => {
          values.forEach(inventory => {
            acc.push(inventory);
          });
          return acc;
        }, []),
    [bundleInventories]
  );

  return (
    <CustomTableVirtuoso
      items={[...inventoryList, ...promotionList, ...extraList, ...bundleInventoryList]}
      handleQuickUpdate={handleQuickUpdate}
      updatedARIValues={updatedARIValues}
      enableEdition={enableEdition}
      handleInputChange={handleInputChange}
      nameType={nameType}
      showAltStyles={showAltStyles}
      numberOfDays={numberOfDays}
    />
  );
};
